import { Spinner } from '@/shared/components/Spinner';
import { TRACE_LOOKBEHIND_MS } from '@/features/admin/lib/logsLink';
import { LogFilters } from './LogFilters';
import { LogEmptyState, LogErrorState, LogScanNotice } from './LogScanNotice';
import { LogTable } from './LogTable';
import { useServerLogs } from './useServerLogs';

/** A trace's lines sit within minutes either side of any one of them. */
const TRACE_LOOKAHEAD_MS = TRACE_LOOKBEHIND_MS;

/**
 * Admin → Logs: recent server log entries, newest first, read from the API's rolling log files.
 * Filters live in the URL so a toast can deep-link to one request (?traceId=…) and a reload
 * keeps the view. Default view: Warning and above, last 24 hours.
 */

export function AdminLogs() {
  const { filters, updateFilters, items, nextCursor, scan, source, loading, loadingMore, error, refresh, loadOlder } =
    useServerLogs();

  // A trace is a handful of lines around one request: drop the search, and bound the window
  // around the row so the scan stops there instead of reading every retained file.
  const showTrace = (traceId: string, timestamp?: string) => {
    const at = timestamp ? Date.parse(timestamp) : NaN;
    const bounded = Number.isFinite(at);
    updateFilters({
      traceId,
      window: undefined,
      from: bounded ? new Date(at - TRACE_LOOKBEHIND_MS).toISOString() : undefined,
      to: bounded ? new Date(at + TRACE_LOOKAHEAD_MS).toISOString() : undefined,
      q: undefined,
    });
  };

  const firstLoad = loading && scan === null && !error;

  return (
    <div className="space-y-4">
      <LogFilters filters={filters} loading={loading} onChange={updateFilters} onRefresh={() => void refresh()} />

      {firstLoad ? (
        <div className="flex h-48 items-center justify-center">
          <Spinner className="h-8 w-8 text-primary" />
        </div>
      ) : (
        <>
          <LogScanNotice scan={scan} />
          {error && items.length === 0 ? (
            <LogErrorState message={error} loading={loading} onRetry={() => void refresh()} />
          ) : items.length > 0 && source?.available !== false ? (
            <div className={loading ? 'opacity-60 transition-opacity' : undefined} aria-busy={loading}>
              <LogTable items={items} onShowTrace={showTrace} />
            </div>
          ) : (
            <LogEmptyState source={source} hasItems={items.length > 0} />
          )}
          {nextCursor && (
            <div className="flex justify-center">
              <button type="button" onClick={() => void loadOlder()} disabled={loadingMore || loading} className="btn-outline gap-2">
                {loadingMore && <Spinner />}
                {loadingMore ? 'Loading…' : 'Load older'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
